"use client";

import { useEffect, useRef } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { currentMarketingTrafficContext } from "@/lib/analytics-traffic";
import {
  isMarketingAnalyticsAllowed,
  readCookieYesConsentState,
  sameCookieYesConsentState,
  type CookieYesConsentState,
} from "@/lib/marketing-consent";
import {
  captureMarketingPageview,
  syncMarketingAnalyticsConsentMode,
} from "@/lib/marketing-analytics-client";
import { siteConfig } from "@/lib/site-config";

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    hj?: (...args: unknown[]) => void;
  }
}

function captureGooglePageview() {
  if (typeof window.gtag !== "function") {
    return null;
  }

  const trafficContext = currentMarketingTrafficContext(siteConfig.posthog.environment);
  const pageLocation = `${window.location.origin}${trafficContext.page_path_query_redacted}`;

  window.gtag("event", "page_view", {
    page_title: document.title,
    page_location: pageLocation,
    ...trafficContext,
    page_path: trafficContext.page_path_query_redacted,
    analytics_page_path: trafficContext.analytics_page_path,
  });

  return trafficContext.page_path_query_redacted;
}

export function ConsentManagedScripts() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const search = searchParams.toString();
  const lastGoogleTrackedPathRef = useRef<string | null>(null);
  const lastPostHogTrackedPathRef = useRef<string | null>(null);
  const lastConsentStateRef = useRef<CookieYesConsentState | null>(null);

  useEffect(() => {
    const syncConsentState = () => {
      const consentState = readCookieYesConsentState();

      if (sameCookieYesConsentState(lastConsentStateRef.current, consentState)) {
        return;
      }

      const analyticsAllowed = isMarketingAnalyticsAllowed(consentState);

      window.gtag?.("consent", "update", {
        analytics_storage: analyticsAllowed ? "granted" : "denied",
        ad_storage: "denied",
        ad_user_data: "denied",
        ad_personalization: "denied",
      });

      if (analyticsAllowed) {
        const trafficContext = currentMarketingTrafficContext(siteConfig.posthog.environment);

        if (lastGoogleTrackedPathRef.current !== trafficContext.page_path_query_redacted) {
          const pagePath = captureGooglePageview();

          if (pagePath) {
            lastGoogleTrackedPathRef.current = pagePath;
          }
        }
      }

      lastConsentStateRef.current = consentState;

      if (consentState.hasExplicitAction) {
        void syncMarketingAnalyticsConsentMode().then((modeChanged) => {
          if (modeChanged) {
            lastConsentStateRef.current = consentState;
          }
        });
      }
    };

    syncConsentState();

    const intervalId = window.setInterval(syncConsentState, 1000);
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        syncConsentState();
      }
    };

    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, []);

  useEffect(() => {
    if (typeof window.gtag !== "function") {
      return;
    }

    const trafficContext = currentMarketingTrafficContext(siteConfig.posthog.environment);
    const pagePath = trafficContext.page_path_query_redacted;
    const pageLocation = `${window.location.origin}${pagePath}`;

    if (lastGoogleTrackedPathRef.current === pagePath) {
      return;
    }

    window.gtag("event", "page_view", {
      page_title: document.title,
      page_location: pageLocation,
      ...trafficContext,
      page_path: trafficContext.page_path_query_redacted,
      analytics_page_path: trafficContext.analytics_page_path,
    });

    window.hj?.("stateChange", pagePath);
    lastGoogleTrackedPathRef.current = pagePath;
  }, [pathname, search]);

  useEffect(() => {
    const pagePath = currentMarketingTrafficContext(
      siteConfig.posthog.environment,
    ).page_path_query_redacted;

    if (lastPostHogTrackedPathRef.current === pagePath) {
      return;
    }

    captureMarketingPageview();
    lastPostHogTrackedPathRef.current = pagePath;
  }, [pathname, search]);

  return null;
}
